import React, {useCallback, useEffect, useMemo, useState} from "react";
import useIsMounted from "../../../../../hooks/use-is-mounted";
import Api from "../../../../../../core/services/api/api";
import {ReactComponent as NoTasksFound} from "../../../../../../assets/images/art-like/no-tasks-found.svg"
import {useNotebookConfig} from "../../../../../hooks/use-notebook-config";
import {useDispatch} from "react-redux";
import ReduxStoreRoutes from "../../../../../../core/models/static/routes/redux-store-routes";
import {setReduxDialogState} from "../../../../../redux/slices/dialogs/actions";
import {TaskStatusTypes} from "../../../../../../core/constants/enums";
import Button from "../../../../base/button/button";
import DraggableSwitch from "../../../../base/switch/draggable-switch";
import classnames from "classnames";
import LazyImage from "../../../lazy-image";
import DateUtils from "../../../../../../core/services/date-utils";
import {ReactComponent as ImagePlaceholder} from "../../../../../../assets/images/operator-profile-placeholder/operator-profile-placeholder-sidebar.svg";
import CacheService from "../../../../../../core/services/cache/cache_service";
import {useOperatorInformation} from "../../../../../hooks/use-operator-information";

const FloatingActionButtonNotebookContentTasks = () => {
    const [loading, setLoading] = useState(true);
    const [statuses, setStatuses] = useState([]);
    const [tasks, setTasks] = useState([]);
    const {tasksConfig} = useNotebookConfig();
    const isMounted = useIsMounted();
    const dispatch = useDispatch();
    const operatorInformation = useOperatorInformation();

    /**
     * Fetches the task statuses from the api
     * @type {(function(): Promise<void>)}
     */
    const getTaskStatuses = useCallback(async () => {
        const response = await Api.getTaskStatuses();
        if (!isMounted())
            return;
        setStatuses(response?.data ?? []);
    }, [isMounted])

    /**
     * With initial render of the component, fetches the task statuses.
     */
    useEffect(() => {
        getTaskStatuses().then();
    }, [getTaskStatuses]);

    /**
     * Fetches the notes for the current page url
     * @type {(function(*[]): Promise<[]>)}
     */
    const getTagsForTagGroup = useCallback(async (tagGroup) => {
        const size = 20;
        const _response = await Api.getPageTasksV2({
            tags: tagGroup,
            pagination: {currentPage: 1, pageSize: size}
        });
        if (!isMounted())
            return []
        if (_response?.resultFlag) {
            if (_response?.data?.totalCount > size) {
                const response = await Api.getPageTasksV2({
                    tags: tagGroup,
                    pagination: {currentPage: 1, pageSize: Math.max(_response?.data?.totalCount ?? 0, 1)}
                });
                if (!isMounted())
                    return []
                return response?.data?.items ?? []
            } else {
                return _response?.data?.items ?? []
            }
        }
        return []
    }, [isMounted])


    /**
     * Fetches the notes for the current page url
     * @type {(function(): Promise<void>)}
     */
    const getNotesForThisPage = useCallback(async () => {
        setLoading(true);
        const promises = tasksConfig.tagGroups.map(e => getTagsForTagGroup(e));
        const groupsNotes = await Promise.all(promises)
        if (!isMounted())
            return;
        setTasks(groupsNotes.flat());
        setLoading(false);
    }, [getTagsForTagGroup, isMounted, tasksConfig.tagGroups])

    /**
     * With initial render of the component and changes in the location:
     * Calls the function responsible for getting the list of notes
     */
    useEffect(() => {
        getNotesForThisPage().then();
    }, [getNotesForThisPage])


    /**
     * Adds a new task for current url in the system
     */
    const addNewTask = useCallback(async () => {
        dispatch(setReduxDialogState(ReduxStoreRoutes.dialogs.upsertTask,
            {
                open: true,
                task: null,
                tags: tasksConfig.tagGroups[0],
                onSuccess: (task) => {
                    tasksConfig.onTaskUpsert(task);
                    if (task.assignedToOperator?.id === operatorInformation?.id) {
                        setTasks((prevState) => [...prevState, task])
                    }
                },
            }))
    }, [dispatch, operatorInformation?.id, tasksConfig])


    /**
     * Toggles the task done status.
     * @type {(function(*, *): Promise<void>)}
     */
    const toggleTaskDoneStatus = useCallback(async (task, done) => {
        const forApi = {
            taskId: task?.id,
            taskStatusId: done
                ? statuses.find(e => e.statusType === TaskStatusTypes.Done)?.id :
                statuses.find(e => e.statusType === TaskStatusTypes.ToDo)?.id,
        }
        const response = await Api.changeTaskStatus(forApi);
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            setTasks(prevState => {
                const result = prevState.map(e => e.id === task.id ? {...e, taskStatus: response?.data} : e)
                tasksConfig.onTaskUpsert(result.find(e => e.id === task.id));
                return result;
            });
        }
        return response;
    }, [isMounted, statuses, tasksConfig])


    /**
     * Sets the task with the received [taskId] to the received [callbackOrTask]
     * @type {(function(number, function | any): void)}
     */
    const setTask = useCallback((taskId, callbackOrTask) => {
        setTasks(prevState => {
            let result = [];
            if (typeof callbackOrTask === 'function') {
                const found = prevState.find(e => e.id === taskId);
                if (!found)
                    return prevState;
                result = prevState.map(e => e.id === taskId ? callbackOrTask(found) : e);
            } else {
                result = prevState.map(e => e.id === taskId ? callbackOrTask : e);
            }
            tasksConfig.onTaskUpsert(result.find(e => e.id === taskId));
            return result;
        })
    }, [tasksConfig])

    return (
        <div className={'floaty-notebook-panel-content'}>
            <div className={'floaty-notebook-panel-scroll'}>
                {
                    loading
                        ? <div className={'tasks-wrapper'}>
                            {
                                Array(10).fill(null).map((_, index) =>
                                    <div key={index} className={'loading normal mb-3 mx-2'}>
                                        <div/>
                                    </div>
                                )
                            }
                        </div>
                        : tasks.length < 1
                            ? <div className={'floaty-notebook-empty-state'}>
                                <NoTasksFound/>
                            </div>
                            : <div className={'tasks-wrapper'}>
                                {
                                    tasks.map(e =>
                                        <TaskCard
                                            key={e.id}
                                            task={e}
                                            setTask={(...args) => setTask(e.id, ...args)}
                                            toggleTaskDoneStatus={toggleTaskDoneStatus}
                                        />
                                    )
                                }
                            </div>
                }
            </div>
            <div className={'w-100 d-flex'}>
                <Button
                    disabled={loading}
                    className={'add-button'}
                    color={'primary'}
                    onClick={addNewTask}
                >
                    Add a new task
                </Button>
            </div>
        </div>
    );
}


const TaskCard = ({task, toggleTaskDoneStatus, setTask}) => {
    const [updating, setUpdating] = useState(false);
    const dispatch = useDispatch();
    const isMounted = useIsMounted();
    const operatorInformation = useOperatorInformation();
    const createdByProfileImageUrl = useMemo(() => CacheService.getOperatorProfileImage(task?.createdByOperator?.id), [task?.createdByOperator?.id]);
    const selfAssigned = useMemo(() => task?.createdByOperator?.id === operatorInformation?.id, [operatorInformation?.id, task?.createdByOperator?.id]);

    /**
     * Toggles task being done:
     * Calls the api regarding this action
     * if the api call was successful, updates the task's [isDone] property in the inner state
     * @param {boolean} done
     */
    const toggleTaskBeingDone = async (done) => {
        setUpdating(true);
        await toggleTaskDoneStatus(task, done);
        if (!isMounted())
            return;
        setUpdating(false);

    }

    /**
     * Opens the task more info dialog
     */
    const openTaskFullInfoDialog = () => {
        dispatch(setReduxDialogState(ReduxStoreRoutes.dialogs.taskMoreInformation, {
            open: true,
            task: task,
            toggleTaskDoneStatus: toggleTaskDoneStatus,
            setTask: setTask,
        }))
    }

    return (
        <div className={'task-card'} onClick={openTaskFullInfoDialog}>
            <div className={'task-title'}>
                <div className={'title'}>
                    <p>
                        {task?.title}
                    </p>
                </div>
                <DraggableSwitch
                    activeTitle={'DONE'}
                    inActiveTitle={'TO DO'}
                    size={4}
                    disabled={updating}
                    checked={task?.taskStatus?.statusType === TaskStatusTypes.Done}
                    onChange={(checked) => toggleTaskBeingDone(checked)}
                />
            </div>
            <div className={'task-info'}>
                <div className={classnames('assigned-container', {'self-assigned': selfAssigned})}>
                    <div className={'assigner-image-container'}>
                        <LazyImage
                            alt={'assigner image'}
                            noImage={<ImagePlaceholder/>}
                            src={createdByProfileImageUrl}
                            height={27}
                        />
                    </div>
                    <p className={'assigner-text'}>
                        Assigned by <span
                        className={'assigner-name'}>{task?.createdByOperator?.firstname ?? ''} {task?.createdByOperator?.lastname ?? ''}</span>
                    </p>
                </div>
                <div className={'due-date-container'}>
                    {
                        task?.dueDate &&
                        <p className={'due-date'}>
                            Due date: <span className={'date'}>{DateUtils.serverToLocal(task?.dueDate).format(DateUtils.dateFormat)}</span>
                        </p>
                    }
                </div>
            </div>
        </div>
    );
}

export default FloatingActionButtonNotebookContentTasks;
